import { NonIdealState } from "@blueprintjs/core";
import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { LocalUserState, MediaDevicesState, PerformanceState, RemoteVideoLayoutOptions, RoomState } from "../../common";
import { JitsiManagerChildProps } from "../data/JitsiManager";
import { SelfView } from "../ui";
import FloatingWindow from "./FloatingWindow";
import styles from "./PerformerGrid.module.css";

export interface PerformerGridProps extends JitsiManagerChildProps {
    roomState: RoomState;
    performance: PerformanceState;
    mediaDevices: MediaDevicesState;
    localUser: LocalUserState;
}

const NUM_PERFORMERS = 4;

@observer
export default class PerformerGrid extends React.PureComponent<PerformerGridProps> {
    private getVideoRefHandler = (stream: MediaStream) => (el: HTMLVideoElement) => {
        if (el != null) {
            el.srcObject = stream;
        }
    };

    public render() {
        return <div className={styles.container}>{[...Array(NUM_PERFORMERS).keys()].map(this.renderTile)}</div>;
    }

    private renderTile = (idx: number) => {
        const { roomState, localUser, performance } = this.props;
        const userId = performance.userLayout.get(idx + 1);

        if (userId === undefined) {
            // no one joined as this performer yet
            return (
                <div className={styles.tile} key={idx}>
                    <NonIdealState title={`Performer ${idx + 1}`} icon="mugshot" />
                </div>
            );
        } else {
            if (localUser.userId === userId) {
                // self view
                return (
                    <div className={styles.tile} key={userId}>
                        <FloatingWindow
                            title={localUser.displayName}
                            className={this.getFloatingWindowClasses(localUser.displayName)}
                        >
                            <SelfView {...this.props} showOptions={false} responsiveWidth={false} />
                        </FloatingWindow>
                    </div>
                );
            } else {
                const layout = roomState.videoLayout.find((l) => l.userId === userId);
                return (
                    <div className={styles.tile} key={userId}>
                        {this.maybeRenderRemotePerformer(layout)}
                    </div>
                );
            }
        }
    };

    private maybeRenderRemotePerformer = (layout?: RemoteVideoLayoutOptions) => {
        // performer may have joined, but not uploaded tracks yet, so include this guard
        if (layout === undefined) {
            return;
        }

        const { room, roomState } = this.props;

        if (layout !== undefined && roomState.tracks.has(layout.userId)) {
            const track = roomState.tracks.get(layout.userId)!.find((t) => t.getType() === "video");
            const participant = room.getParticipantById(layout.userId);
            if (track !== undefined && participant !== undefined) {
                const displayName = roomState.displayNames.get(layout.userId) ?? participant.getDisplayName();

                return (
                    <FloatingWindow title={displayName} className={this.getFloatingWindowClasses(displayName)}>
                        <video
                            className={styles.remoteVideo}
                            ref={this.getVideoRefHandler(track.stream)}
                            autoPlay={true}
                            playsInline={true}
                            muted={true}
                        />
                    </FloatingWindow>
                );
            }
        }
    };

    private getFloatingWindowClasses = (displayName = "") => {
        const { performance } = this.props;

        const isHighlighted = performance.sceneMentionedUsers.get(performance.scene)?.has(displayName);

        let duoClass;
        if (
            performance.sceneDuos !== undefined &&
            performance.scenePrompts.get(performance.scene)?.advanceWithMentions !== "all"
        ) {
            const [duo1, duo2] = performance.sceneDuos;
            if (duo1.indexOf(displayName) >= 0) {
                duoClass = "highlightDuo1";
            } else if (duo2.indexOf(displayName) >= 0) {
                duoClass = "highlightDuo2";
            }
        }

        return classNames(duoClass, {
            isHighlighted: duoClass === undefined && isHighlighted,
        });
    };
}
